import React, { useState, useEffect } from 'react';
import Dialog from '../Dialog/Dialog';
import Button from '../Button/Button';
import Input from '../Input/Input';
import DeleteButton from '../DeleteButton/DeleteButton';
import './EditTeamDialog.css';

interface EditTeamDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  isLoading: boolean;
  initialName: string;
}

const EditTeamDialog: React.FC<EditTeamDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  isLoading,
  initialName
}) => {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setError('');
      setIsDeleting(false);
    }
  }, [isOpen, initialName]);

  const handleSubmit = async () => {
    setError('');
    
    if (!name.trim()) {
      setError('Team name is required');
      return;
    }

    try {
      await onSave(name.trim());
      setError('');
    } catch (err) {
      setError('Failed to update team');
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      if (onDelete) {
        await onDelete();
      }
      onClose();
    } catch (err) {
      setError('Failed to delete team');
      setIsDeleting(false);
    }
  };

  const headerActions = (
    <div className="dialog-header-actions">
      <Button 
        variant="secondary" 
        onClick={onClose}
        disabled={isLoading || isDeleting}
        size="small"
      >
        Cancel
      </Button>
      <Button 
        variant="primary" 
        onClick={handleSubmit}
        disabled={isLoading || isDeleting || name.trim() === initialName}
        isLoading={isLoading}
        size="small"
      >
        Save
      </Button>
    </div>
  );

  const dialogActions = onDelete && (
    <div className="dialog-actions-left">
      <DeleteButton
        onDelete={handleDelete}
        disabled={isLoading}
        size="small"
      />
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Team"
      actions={dialogActions}
      headerActions={headerActions}
      showCloseButton={false}
    >
      <div className="edit-team-dialog">
        <Input
          label="Team Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError('');
          }}
          placeholder="Enter team name"
          error={error}
          disabled={isLoading || isDeleting}
          required
        />
      </div>
    </Dialog>
  );
};

export default EditTeamDialog; 